import { useEffect, useCallback, useRef } from "react";

/**
 * Keyboard shortcut configuration
 */
export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  description: string;
  handler: (event: KeyboardEvent) => void;
  preventDefault?: boolean;
}

/**
 * Check if keyboard event matches shortcut configuration
 */
function matchesShortcut(event: KeyboardEvent, shortcut: KeyboardShortcut): boolean {
  const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
  const ctrlMatches = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
  const shiftMatches = shortcut.shift ? event.shiftKey : !event.shiftKey;
  const altMatches = shortcut.alt ? event.altKey : !event.altKey;

  return keyMatches && ctrlMatches && shiftMatches && altMatches;
}

/**
 * Hook to register keyboard shortcuts
 */
export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[]) {
  const shortcutsRef = useRef<KeyboardShortcut[]>(shortcuts);

  // Update ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't trigger shortcuts when typing in inputs
    const target = event.target as HTMLElement;
    const isInput =
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable;

    // Allow Escape key even in inputs
    // Also allow Alt+key shortcuts even in inputs for navigation
    if (isInput && event.key !== "Escape" && !event.altKey) {
      return;
    }

    // Check each shortcut
    for (const shortcut of shortcutsRef.current) {
      if (matchesShortcut(event, shortcut)) {
        if (shortcut.preventDefault !== false) {
          event.preventDefault();
        }
        shortcut.handler(event);
        break;
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Format shortcut for display
 */
export function formatShortcut(shortcut: KeyboardShortcut): string {
  const parts: string[] = [];

  if (shortcut.ctrl) parts.push("Ctrl");
  if (shortcut.shift) parts.push("Shift");
  if (shortcut.alt) parts.push("Alt");
  if (shortcut.meta) parts.push("⌘");

  parts.push(shortcut.key.toUpperCase());

  return parts.join(" + ");
}

/**
 * Global shortcuts that work across the entire app
 */
export const GLOBAL_SHORTCUTS = {
  // Search
  SEARCH: { key: "k", ctrl: true, description: "ເປີດການຄົ້ນຫາ" },
  FOCUS_SEARCH: { key: "/", description: "ໂຟກັດໃສ່ການຄົ້ນຫາ" },
  
  // Navigation
  GO_DASHBOARD: { key: "d", alt: true, description: "ໄປໜ້າ Dashboard" },
  GO_CUSTOMERS: { key: "c", alt: true, description: "ໄປລາຍການລູກຄ້າ" },
  GO_LOANS: { key: "l", alt: true, description: "ໄປຄຳຂໍສິນເຊື່ອ" },
  GO_REPORTS: { key: "r", alt: true, description: "ໄປລາຍງານ" },
  GO_SETTINGS: { key: "s", alt: true, description: "ໄປຕັ້ງຄ່າ" },
  
  // Actions
  NEW_CUSTOMER: { key: "n", ctrl: true, description: "ເພີ່ມລູກຄ້າໃໝ່" },
  TOGGLE_THEME: { key: "t", alt: true, description: "ປ່ຽນ Dark/Light Mode" },
  
  // General
  CLOSE_MODAL: { key: "Escape", description: "ປິດ modal/dialog" },
  SAVE: { key: "s", ctrl: true, description: "ບັນທຶກ" },
  REFRESH: { key: "r", ctrl: true, shift: true, description: "ໂຫຼດຂໍ້ມູນໃໝ່" },
  HELP: { key: "?", shift: true, description: "ສະແດງຄວາມຊ່ວຍເຫຼືອ" },
} as const;

/**
 * Get all shortcuts as array for display
 */
export function getAllShortcuts(): { category: string; shortcuts: { keys: string; description: string }[] }[] {
  return [
    {
      category: "ການນຳທາງ (Navigation)",
      shortcuts: [
        { keys: "Alt + D", description: "ໄປໜ້າ Dashboard" },
        { keys: "Alt + C", description: "ໄປລາຍການລູກຄ້າ" },
        { keys: "Alt + L", description: "ໄປຄຳຂໍສິນເຊື່ອ" },
        { keys: "Alt + R", description: "ໄປລາຍງານ" },
        { keys: "Alt + S", description: "ໄປຕັ້ງຄ່າ" },
        { keys: "Ctrl + N", description: "ເພີ່ມລູກຄ້າໃໝ່" },
      ],
    },
    {
      category: "ການຄົ້ນຫາ (Search)",
      shortcuts: [
        { keys: "Ctrl + K", description: "ເປີດການຊອກຫາ" },
        { keys: "/", description: "ໂຟກັດໃສ່ການຊອກຫາ" },
      ],
    },
    {
      category: "ທົ່ວໄປ (General)",
      shortcuts: [
        { keys: "Alt + T", description: "ປ່ຽນ Dark/Light Mode" },
        { keys: "Ctrl + /", description: "ສະແດງ Keyboard Shortcuts" },
        { keys: "Shift + ?", description: "ສະແດງ Keyboard Shortcuts" },
        { keys: "Esc", description: "ປິດ Dialog/Modal" },
        { keys: "Ctrl + S", description: "ບັນທຶກຟອມ" },
      ],
    },
  ];
}
